import { HOMEPAGE_URL } from '../../helpers/globals';
import { toImagesWorkerSourceUrl } from '../../helpers/imageUrl';

export const HOMES_MARKET_PROOF =
  'Tour on your schedule. Check the listing for special pricing and incentives.';
export const HOMES_MARKET_TITLE_PROOF = 'Tour one today.';

const HUB_CANONICAL = '/homes';

export type HomesMarketSeoInput = {
  canonicalPath: string;
  heroPhotoUrl?: string | null;
  isHubPage: boolean;
  listingCount?: number | null;
  locationText: string;
  marketDisplayName: string;
  marketSlug: string;
  origin?: string;
};

export type HomesMarketSeo = {
  canonicalUrl: string;
  cardDescription: string;
  cardTitle: string;
  description: string;
  ogImageAlt: string;
  ogImageUrl: string;
  ogTitle: string;
  title: string;
};

const formatListingCount = (count: number): string => new Intl.NumberFormat('en-US').format(count);

export const isAllowedOgPhotoUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'opendoor.com' ||
        parsed.hostname.endsWith('.opendoor.com') ||
        parsed.hostname === 'simplersell.com' ||
        parsed.hostname.endsWith('.simplersell.com'))
    );
  } catch {
    return false;
  }
};

const toAllowedOgPhotoUrl = (url: string): string | null => {
  const workerUrl = toImagesWorkerSourceUrl(url);
  if (workerUrl && isAllowedOgPhotoUrl(workerUrl)) {
    return workerUrl;
  }
  return isAllowedOgPhotoUrl(url) ? url : null;
};

export const pickHomesMarketHeroPhoto = (urls: Array<string | null | undefined>): string | null => {
  for (const url of urls) {
    if (!url) {
      continue;
    }
    const allowed = toAllowedOgPhotoUrl(url);
    if (allowed) {
      return allowed;
    }
  }
  return null;
};

const MARKET_OG_SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const OG_LISTING_COUNT = /^[1-9][0-9]{0,5}$/;

const ogImagePath = (canonicalPath: string, marketSlug: string): string => {
  if (canonicalPath === HUB_CANONICAL) {
    return '/homes/opengraph-image';
  }
  if (MARKET_OG_SLUG.test(marketSlug) && canonicalPath === `/homes/${marketSlug}`) {
    return `/homes/${marketSlug}/opengraph-image`;
  }
  return '/homes/opengraph-image';
};

const positiveListingCount = (isHubPage: boolean, listingCount?: number | null): number | null =>
  !isHubPage && listingCount != null && listingCount > 0 ? listingCount : null;

const formatOgMarketName = (slug: string): string =>
  slug
    .replace(/_/g, '-')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const parseOgListingCount = (raw: string | null): number | null => {
  if (raw == null || !OG_LISTING_COUNT.test(raw)) {
    return null;
  }
  return Number(raw);
};

const buildHomesMarketCardTitle = ({
  isHubPage,
  listingCount,
  marketDisplayName,
}: {
  isHubPage: boolean;
  listingCount?: number | null;
  marketDisplayName: string;
}): string => {
  const count = positiveListingCount(isHubPage, listingCount);
  if (isHubPage) {
    return `Homes for sale. ${HOMES_MARKET_TITLE_PROOF}`;
  }
  return count != null
    ? `${formatListingCount(count)} homes for sale in ${marketDisplayName}. ${HOMES_MARKET_TITLE_PROOF}`
    : `Homes for sale in ${marketDisplayName}. ${HOMES_MARKET_TITLE_PROOF}`;
};

export const resolveHomesMarketOgCard = (params: {
  get: (name: string) => string | null;
}): { description: string; photoUrl: string | null; title: string } => {
  const marketSlug = (params.get('market') ?? '').toLowerCase();
  if (!MARKET_OG_SLUG.test(marketSlug)) {
    return {
      description: HOMES_MARKET_PROOF,
      photoUrl: null,
      title: buildHomesMarketCardTitle({
        isHubPage: true,
        listingCount: null,
        marketDisplayName: '',
      }),
    };
  }

  const photoParam = params.get('photo');
  return {
    description: HOMES_MARKET_PROOF,
    photoUrl: photoParam && isAllowedOgPhotoUrl(photoParam) ? photoParam : null,
    title: buildHomesMarketCardTitle({
      isHubPage: false,
      listingCount: parseOgListingCount(params.get('count')),
      marketDisplayName: formatOgMarketName(marketSlug),
    }),
  };
};

export const buildHomesMarketSeo = ({
  canonicalPath,
  heroPhotoUrl,
  isHubPage,
  listingCount,
  locationText,
  marketDisplayName,
  marketSlug,
  origin = HOMEPAGE_URL,
}: HomesMarketSeoInput): HomesMarketSeo => {
  const count = positiveListingCount(isHubPage, listingCount);
  const canonicalUrl = `${origin}${canonicalPath}`;
  const cardTitle = buildHomesMarketCardTitle({
    isHubPage,
    listingCount: count,
    marketDisplayName,
  });
  const title = `${cardTitle} | Opendoor`;
  let description = `Browse homes for sale in ${locationText}. ${HOMES_MARKET_PROOF}`;
  if (isHubPage) {
    description = `Browse homes for sale across 50+ markets. ${HOMES_MARKET_PROOF}`;
  } else if (count != null) {
    description = `Browse ${formatListingCount(count)} homes for sale in ${locationText}. ${HOMES_MARKET_PROOF}`;
  }
  const photoUrl = !isHubPage && heroPhotoUrl ? toAllowedOgPhotoUrl(heroPhotoUrl) : null;

  return {
    canonicalUrl,
    cardDescription: HOMES_MARKET_PROOF,
    cardTitle,
    description,
    ogImageAlt: `${cardTitle} ${HOMES_MARKET_PROOF}`,
    ogImageUrl: buildHomesMarketOgImageUrl({
      origin,
      path: ogImagePath(canonicalPath, marketSlug),
      photoUrl,
      listingCount: count,
    }),
    ogTitle: title,
    title,
  };
};

const buildHomesMarketOgImageUrl = ({
  listingCount,
  origin,
  path,
  photoUrl,
}: {
  listingCount?: number | null;
  origin: string;
  path: string;
  photoUrl?: string | null;
}): string => {
  const params = new URLSearchParams();
  if (listingCount != null) {
    params.set('count', String(listingCount));
  }
  if (photoUrl) {
    params.set('photo', photoUrl);
  }
  const query = params.toString();
  return query ? `${origin}${path}?${query}` : `${origin}${path}`;
};
